import { useCallback, useEffect } from "react";
import { useAsyncFn } from "react-use";
import { DatadogIncident } from "@internal/plugin-ops-review-common";

export interface Props {
  incidents: DatadogIncident[];
}

type ParsedResponder = {
  name: string;
  filter: boolean;
  type: string;
  id: string;
  link?: string;
  teams?: string[];
};

export const useParsedDatadogIncidents = ({ incidents }: Props) => {
  const getParsedIncidents = useCallback(async () => {
    if (!incidents || incidents.length === 0) {
      return [];
    }

    // Parse Datadog incidents - they already come with most info we need
    return incidents.map((incident: DatadogIncident) => {
      // Convert teams to responders format
      const responders: ParsedResponder[] = incident.teams.map((teamName) => ({
        name: teamName,
        filter: false,
        type: "team",
        id: teamName,
        link: `/catalog/default/group/${teamName}`,
        teams: [teamName],
      }));

      // Transform Datadog incident to match OpsGenie structure for display
      return {
        ...incident,
        // Map Datadog fields to OpsGenie-like structure
        tinyId: String(incident.id), // Convert to string to handle both string and number IDs
        message: incident.title,
        status: incident.state,
        ownerTeam: incident.teams?.[0] || "unknown",
        responders,
        impactedServices: incident.services,
        impactStartDate: incident.createdAt,
        impactEndDate: incident.resolvedAt || "",
        extraProperties: {},
        actions: [],
      };
    });
  }, [incidents]);

  const [state, fetchParsedIncidents] = useAsyncFn(async () => {
    return await getParsedIncidents();
  }, [getParsedIncidents]);

  useEffect(() => {
    fetchParsedIncidents();
  }, [fetchParsedIncidents]);

  return {
    loading: state.loading,
    error: state.error,
    value: state?.value,
    fetchParsedIncidents,
  };
};
